import React, { useState, useEffect } from "react";

import { useAuth } from "../auth/AuthContext";

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import CategoryDropdown from "./CategoryDropdown"; // Assuming this is your custom dropdown component

import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// Constants for allowed file types & max sizes
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB max

// Helper: convert date string to YYYY-MM-DD for input[type=date]
function convertDateToInputFormat(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Sample document categories — replace with actual from your store or API
const documentCategories = [
  { categoryid: 100, categoryname: "NDA" },
  { categoryid: 101, categoryname: "Passport" },
  { categoryid: 102, categoryname: "Offer Letter" },
  { categoryid: 103, categoryname: "Adhaar" },
  // add more as needed
];

// Helper to get display name of doc type by id
const getTypeName = (id) => {
  const cat = documentCategories.find((c) => c.categoryid === Number(id));
  return cat ? cat.categoryname : id;
};

export default function DocumentManager({
  entityType,
  entityId,
  clientId,
  userId,
  onDocumentsChange,
  documentCategoryType = "State", // default if not overridden below
}) {
  switch (entityType) {
    case "employee":
      documentCategoryType = "EmployeeDocument";
      break;
    case "vehicle":
      documentCategoryType = "VehicleDocument";
      break;
    default:
      break;
  }

  const { token } = useAuth();

  const [form, setForm] = useState({
    type: "",
    document_name: "", // <- check if this exists
    number: "",
    expiry: "",
    file: null,
  });

  const [editingIndex, setEditingIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [deletedDocumentIds, setDeletedDocumentIds] = useState([]);

  useEffect(() => {
    if (!entityId || !entityType) {
      setDocuments([]);
      setDeletedDocumentIds([]);
      return;
    }

    async function fetchDocuments() {
      try {
        setLoading(true);
        setErrorMsg(null);

        const res = await fetch(
          `/api/documents/${entityType}/${entityId}?client_id=${clientId}`
        );
        if (!res.ok) throw new Error(`Error: ${res.statusText}`);
        const data = await res.json();

        const formatted = data.map((doc) => ({
          ...doc,
          document_expiry_date: convertDateToInputFormat(
            doc.document_expiry_date
          ),
          isExisting: true,
          isEdited: false,
          file: null,
        }));

        setDocuments(formatted);
        setDeletedDocumentIds([]);
      } catch (err) {
        setErrorMsg(`Failed to load documents: ${err.message}`);
        setDocuments([]);
        setDeletedDocumentIds([]);
      } finally {
        setLoading(false);
      }
    }

    fetchDocuments();
  }, [entityId, entityType, clientId]);

  useEffect(() => {
    if (typeof onDocumentsChange === "function") {
      onDocumentsChange(documents, deletedDocumentIds);
    }
  }, [documents, deletedDocumentIds, onDocumentsChange]);

  // For regular inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: name === "type" ? Number(value) : value,
    }));
  };

  
  const handleTypeChange = (arg) => {
    const value = arg.target.value;
    const label = arg.target.label;

    console.log("Selected category:", value, label);

    setForm((f) => ({
      ...f,
      type: value,
      document_name: label,
    }));
  };

  const validateForm = () => {
    if (process.env.NODE_ENV === "development") {
      console.log({
        type: form.type,
        document_name: form.document_name,
        number: form.number,
        expiry: form.expiry,
      });
    }

    const typeValue =
      typeof form.type === "string" || typeof form.type === "number"
        ? String(form.type).trim()
        : "";
    const documentNameValue =
      typeof form.document_name === "string" ? form.document_name.trim() : "";

    const numberValue =
      typeof form.number === "string" || typeof form.number === "number"
        ? String(form.number).trim()
        : "";

    if (!typeValue || !documentNameValue || !numberValue || !form.expiry) {
      setErrorMsg("Please fill all required fields.");
      return false;
    }

    // Your file validations here...

    setErrorMsg(null);
    return true;
  };

  const handleAddOrUpdate = () => {
    if (!validateForm()) return;

    let fileToUse = form.file;
    if (editingIndex !== -1 && !form.file) {
      fileToUse = documents[editingIndex]?.file || null;
    }

    const newDoc = {
      tempId:
        editingIndex === -1
          ? `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          : undefined,
      type: form.type, // document_type_id (ID or value from dropdown)
      document_name: form.document_name || "", // <-- Added: dropdown label (friendly name)
      document_no: form.number.trim(),
      document_expiry_date: form.expiry,
      file: fileToUse,
      document_id:
        editingIndex !== -1 ? documents[editingIndex].document_id : undefined,
      document_file:
        editingIndex !== -1 && !form.file
          ? documents[editingIndex]?.document_file || ""
          : form.file?.name || null,
      isExisting: editingIndex !== -1 ? true : false,
      isEdited: editingIndex !== -1 ? true : false,
    };

    if (editingIndex !== -1) {
      setDocuments((docs) =>
        docs.map((d, idx) => (idx === editingIndex ? newDoc : d))
      );
      setEditingIndex(-1);
    } else {
      setDocuments((docs) => [...docs, newDoc]);
    }

    setForm({
      type: "",
      document_name: "",
      number: "",
      expiry: "",
      file: null,
    });
  };

  const handleEdit = (idx) => {
    const doc = documents[idx];
    setForm({
      type: doc.type || "",
      document_name: doc.document_name || "",
      number: doc.document_no || "",
      expiry: doc.document_expiry_date || "",
      file: null,
    });
    setEditingIndex(idx);
    setErrorMsg(null);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this document?")) return;

    setDocuments((docs) =>
      docs.filter((doc) => (doc.document_id || doc.tempId) !== id)
    );

    // Track deletion if existing document
    const isExisting = documents.find((doc) => doc.document_id === id);
    if (isExisting) {
      setDeletedDocumentIds((ids) => [...ids, id]);
    }

    if (
      editingIndex !== -1 &&
      (documents[editingIndex].document_id === id ||
        documents[editingIndex].tempId === id)
    ) {
      setEditingIndex(-1);
      setForm({
        type: "",
        document_name: "",
        number: "",
        expiry: "",
        file: null,
      });
    }

    setErrorMsg(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((f) => ({ ...f, file }));
      setErrorMsg(null);
    }
  };

  const isImage = (filename) => /\.(jpg|jpeg|png)$/i.test(filename);
  const isPDF = (filename) => /\.pdf$/i.test(filename);

  const handleClosePreview = () => setSelectedDoc(null);

  return (
    <Box>
      {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}
      {errorMsg && <Box sx={{ color: "red", mb: 1 }}>{errorMsg}</Box>}

      {/* Document Input Form */}
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="nowrap">
        <Box sx={{ width: 300 }}>
          <CategoryDropdown
            clientId={clientId}
            token={token} // now defined
            categoryType={documentCategoryType}
            name="type"
            label="Document Type"
            value={form.type}
            onChange={handleTypeChange}
            size="small"
            sx={{ minWidth: 90, width: 90, maxWidth: 120 }}
          />
        </Box>
        <TextField
          name="number"
          size="small"
          placeholder="Document Number"
          value={form.number}
          onChange={handleInputChange}
        />
        <TextField
          name="expiry"
          type="date"
          size="small"
          value={form.expiry}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          component="label"
          sx={{ whiteSpace: "nowrap" }}
        >
          CHOOSE FILE
          <input hidden type="file" name="file" onChange={handleFileChange} />
        </Button>
        <Box component="span" sx={{ minWidth: 120, fontStyle: "italic" }}>
          {form.file
            ? form.file.name
            : editingIndex === -1
            ? "No file chosen"
            : "(keep existing file)"}
        </Box>
        <Button
          variant="contained"
          color="success"
          onClick={handleAddOrUpdate}
          disabled={loading}
        >
          {editingIndex === -1 ? "+ ADD" : "Update"}
        </Button>
        {editingIndex !== -1 && (
          <Button
            variant="outlined"
            onClick={() => {
              setForm({
                type: "",
                document_name: "",
                number: "",
                expiry: "",
                file: null,
              });
              setEditingIndex(-1);
              setErrorMsg(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
      </Box>

      {/* Documents Table */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Number</TableCell>
            <TableCell>Expiry</TableCell>
            <TableCell>File</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                align="center"
                sx={{ color: "#888", fontStyle: "italic" }}
              >
                No documents added.
              </TableCell>
            </TableRow>
          ) : (
            documents.map((doc, idx) => {
              const key = doc.document_id
                ? `doc-${doc.document_id}`
                : doc.tempId
                ? `temp-${doc.tempId}`
                : `idx-${idx}`;
              return (
                <TableRow key={key}>
                  <TableCell>{doc.document_name}</TableCell>
                  <TableCell>{doc.document_no}</TableCell>
                  <TableCell>
                    {doc.document_expiry_date
                      ? new Date(doc.document_expiry_date).toLocaleDateString(
                          "en-GB"
                        )
                      : ""}
                  </TableCell>

                  <TableCell>
                    {doc.document_file || doc.document_path ? (
                      <>
                        <Tooltip title="View Document">
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                // `http://localhost:5000/${doc.document_path}`, // ✅ open from DB path (when available)
                                `/${doc.document_path}`,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                            sx={{ marginRight: 1 }}
                            disabled={!doc.document_path} // ✅ disable until path exists in DB
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <span style={{ fontSize: "0.9rem", color: "#555" }}>
                          {doc.document_file ||
                            doc.document_path?.split("/").pop()}
                        </span>
                      </>
                    ) : (
                      "No File"
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit Document">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(idx)}
                        disabled={loading}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Document">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleDelete(doc.document_id ?? doc.tempId)
                        }
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Document Preview Dialog */}
      <Dialog
        open={Boolean(selectedDoc)}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedDoc && selectedDoc.document_file ? (
            isPDF(selectedDoc.document_file) ? (
              <iframe
                src={`/api/documents/${selectedDoc.document_id}/download`}
                title="PDF Preview"
                width="100%"
                height="600"
                style={{ border: "none" }}
              />
            ) : isImage(selectedDoc.document_file) ? (
              <img
                src={`/api/documents/${selectedDoc.document_id}/download`}
                alt="Document Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: 600,
                  display: "block",
                  margin: "0 auto",
                }}
              />
            ) : (
              <Box sx={{ p: 2 }}>
                <p>No preview available for this file type.</p>
                <Link
                  href={`/api/documents/${selectedDoc.document_id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download File
                </Link>
              </Box>
            )
          ) : (
            <Box sx={{ p: 2 }}>
              <p>No document selected or file missing.</p>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
